import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DevToolsService } from '../dev-tools.service';
import { JsonTreeComponent } from './json-tree.component';

/**
 * Onglet "State Inspector" — vue temps réel de l'état du module démarches.
 * S'appuie sur `DevToolsService.snapshot()` alimenté par `DemarchesPageComponent`
 * et sur `refreshTick` pour le rafraîchissement auto.
 */
@Component({
  selector: 'dev-state-tab',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, JsonTreeComponent],
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-gray-100">
      <!-- Toolbar -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/60 tw-shrink-0">
        <div class="tw-relative tw-flex-1">
          <i class="ph ph-magnifying-glass tw-absolute tw-left-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500 tw-text-[12px]"></i>
          <input
            type="text"
            [value]="search()"
            (input)="onSearch($event)"
            placeholder="Rechercher une clé ou valeur…"
            class="tw-w-full tw-bg-gray-800/70 tw-border tw-border-gray-700 tw-rounded tw-pl-7 tw-pr-2 tw-py-1 tw-text-[11px] tw-text-gray-200 placeholder:tw-text-gray-500 focus:tw-outline-none focus:tw-border-success-500"
          />
        </div>

        <div class="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-400">
          <span>Arborescence :</span>
          <select
            [value]="expandDepth()"
            (change)="onExpandDepthChange($event)"
            class="tw-bg-gray-800 tw-border tw-border-gray-700 tw-rounded tw-px-1.5 tw-py-1 tw-text-[11px] tw-text-gray-200 focus:tw-outline-none focus:tw-border-success-500"
          >
            <option [value]="1">Niveau 1</option>
            <option [value]="2">Niveau 2 (défaut)</option>
            <option [value]="3">Niveau 3</option>
            <option [value]="4">Niveau 4</option>
            <option [value]="10">Tout déplier</option>
          </select>
        </div>

        <label class="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-gray-400 tw-cursor-pointer tw-select-none">
          <input
            type="checkbox"
            [checked]="autoRefresh()"
            (change)="onToggleAutoRefresh($event)"
            class="tw-accent-success-500"
          />
          Auto-refresh
        </label>

        <button
          type="button"
          class="tw-text-[11px] tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700 tw-flex tw-items-center tw-gap-1"
          (click)="manualRefresh()"
          title="Rafraîchir maintenant"
        >
          <i class="ph ph-arrow-clockwise tw-text-[12px]"></i> Refresh
        </button>

        <button
          type="button"
          class="tw-text-[11px] tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700 tw-flex tw-items-center tw-gap-1"
          (click)="copyAll()"
          title="Copier le snapshot JSON complet"
        >
          <i class="ph ph-copy tw-text-[12px]"></i> Copy
        </button>

        <button
          type="button"
          class="tw-text-[11px] tw-px-2 tw-py-1 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-text-gray-200 tw-border tw-border-gray-700 tw-flex tw-items-center tw-gap-1"
          (click)="download()"
          title="Télécharger en .json"
        >
          <i class="ph ph-download-simple tw-text-[12px]"></i> .json
        </button>
      </div>

      <!-- Last update indicator -->
      <div class="tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-1.5 tw-text-[10px] tw-text-gray-500 tw-border-b tw-border-gray-800 tw-shrink-0 tw-bg-gray-900/40">
        <span class="tw-flex tw-items-center tw-gap-1">
          <span class="tw-w-1.5 tw-h-1.5 tw-rounded-full" [ngClass]="autoRefresh() ? 'tw-bg-success-500' : 'tw-bg-gray-600'"></span>
          Dernière maj : <span class="tw-text-gray-300">{{ ageLabel() }}</span>
        </span>
        <span>Capturé : {{ capturedAtLabel() }}</span>
        <span class="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-semibold"
              [ngClass]="contextLabel() === 'Guest / Consultation' ? 'tw-bg-info-500/20 tw-text-info-400' : 'tw-bg-gray-800 tw-text-gray-300'">
          {{ contextLabel() }}
        </span>
        <span class="tw-ml-auto tw-text-gray-400">
          <i class="ph ph-stack"></i> {{ counts().tasks }} tasks · {{ counts().payments }} payments · {{ counts().notifications }} notifs
        </span>
      </div>

      <!-- Sections -->
      <div class="tw-flex-1 tw-overflow-auto tw-px-3 tw-py-2 tw-space-y-3">
        <!-- Section : Démarche sélectionnée -->
        <section class="tw-rounded tw-border tw-border-gray-700/60 tw-bg-gray-900/50">
          <header class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-800/40">
            <i class="ph ph-file-text tw-text-success-500 tw-text-[13px]"></i>
            <h3 class="tw-text-[12px] tw-font-semibold tw-text-gray-200">Démarche sélectionnée</h3>
            @if (demarcheRef()) {
              <span class="tw-text-[10px] tw-text-success-500 tw-font-mono">{{ demarcheRef() }}</span>
            } @else {
              <span class="tw-text-[10px] tw-text-gray-500 tw-italic">aucune</span>
            }
          </header>
          <div class="tw-p-2 tw-overflow-x-auto">
            @if (snapshot().demarche) {
              <dev-json-tree [value]="snapshot().demarche" [search]="search()" [maxExpandDepth]="expandDepth()" [defaultExpanded]="true" />
            } @else {
              <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-2 tw-py-1">
                Aucune démarche sélectionnée. Ouvre la page Démarches ou Consultation pour peupler ce panel.
              </div>
            }
          </div>
        </section>

        <!-- Section : Tasks -->
        <section class="tw-rounded tw-border tw-border-gray-700/60 tw-bg-gray-900/50">
          <header class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-800/40">
            <i class="ph ph-list-checks tw-text-success-500 tw-text-[13px]"></i>
            <h3 class="tw-text-[12px] tw-font-semibold tw-text-gray-200">Tasks</h3>
            <span class="tw-text-[10px] tw-text-gray-400">({{ snapshot().tasks.length }})</span>
          </header>
          <div class="tw-divide-y tw-divide-gray-800">
            @for (task of snapshot().tasks; track $index) {
              <div class="tw-px-2 tw-py-1.5">
                <button
                  type="button"
                  class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-text-left hover:tw-bg-gray-800/40 tw-rounded tw-px-1 tw-py-0.5"
                  (click)="toggleTask($index)"
                >
                  <i class="ph tw-text-[11px] tw-text-gray-400"
                     [ngClass]="isTaskOpen($index) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                  <span class="tw-text-[11px] tw-text-gray-200 tw-font-medium tw-truncate">{{ taskField(task, 'name') || taskField(task, 'title') || '(sans nom)' }}</span>
                  <span class="tw-text-[9px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-mono"
                        [style.background]="statusBg(taskField(task, 'statusColor'))"
                        [style.color]="statusFg(taskField(task, 'statusColor'))">
                    {{ taskField(task, 'status') || '—' }}
                  </span>
                  <span class="tw-text-[9px] tw-text-gray-500 tw-font-mono">{{ taskField(task, 'handlerType') || '—' }}</span>
                  <span class="tw-text-[9px] tw-text-gray-500 tw-font-mono">{{ taskField(task, 'bpmnType') || '—' }}</span>
                  <span class="tw-ml-auto tw-text-[9px] tw-text-gray-500 tw-font-mono">{{ fmtDate(taskField(task, 'createdAt')) }}</span>
                </button>
                @if (isTaskOpen($index)) {
                  <div class="tw-pl-4 tw-pt-1">
                    <dev-json-tree [value]="task" [search]="search()" [maxExpandDepth]="expandDepth()" [defaultExpanded]="true" />
                  </div>
                }
              </div>
            } @empty {
              <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-3 tw-py-2">Aucune task.</div>
            }
          </div>
        </section>

        <!-- Section : Payments -->
        <section class="tw-rounded tw-border tw-border-gray-700/60 tw-bg-gray-900/50">
          <header class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-800/40">
            <i class="ph ph-credit-card tw-text-success-500 tw-text-[13px]"></i>
            <h3 class="tw-text-[12px] tw-font-semibold tw-text-gray-200">Payments</h3>
            <span class="tw-text-[10px] tw-text-gray-400">({{ snapshot().payments.length }})</span>
          </header>
          <div class="tw-divide-y tw-divide-gray-800">
            @for (pay of snapshot().payments; track $index) {
              <div class="tw-px-2 tw-py-1.5">
                <button
                  type="button"
                  class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-text-left hover:tw-bg-gray-800/40 tw-rounded tw-px-1 tw-py-0.5"
                  (click)="togglePayment($index)"
                >
                  <i class="ph tw-text-[11px] tw-text-gray-400"
                     [ngClass]="isPaymentOpen($index) ? 'ph-caret-down' : 'ph-caret-right'"></i>
                  <span class="tw-text-[11px] tw-text-gray-200 tw-font-medium">{{ taskField(pay, 'status') || '—' }}</span>
                  <span class="tw-text-[10px] tw-text-warning-500 tw-font-mono">
                    {{ taskField(pay, 'amount') ?? '—' }} {{ taskField(pay, 'currency') || '' }}
                  </span>
                  <span class="tw-ml-auto tw-text-[9px] tw-text-gray-500 tw-font-mono">{{ fmtDate(taskField(pay, 'createdAt')) }}</span>
                </button>
                @if (isPaymentOpen($index)) {
                  <div class="tw-pl-4 tw-pt-1">
                    <dev-json-tree [value]="pay" [search]="search()" [maxExpandDepth]="expandDepth()" [defaultExpanded]="true" />
                  </div>
                }
              </div>
            } @empty {
              <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-3 tw-py-2">Aucun paiement.</div>
            }
          </div>
        </section>

        <!-- Section : Notifications -->
        <section class="tw-rounded tw-border tw-border-gray-700/60 tw-bg-gray-900/50">
          <header class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-800/40">
            <i class="ph ph-bell tw-text-success-500 tw-text-[13px]"></i>
            <h3 class="tw-text-[12px] tw-font-semibold tw-text-gray-200">Notifications</h3>
            <span class="tw-text-[10px] tw-text-gray-400">({{ snapshot().notifications.length }})</span>
          </header>
          <div class="tw-p-2">
            @if (snapshot().notifications.length) {
              <dev-json-tree [value]="snapshot().notifications" [search]="search()" [maxExpandDepth]="expandDepth()" [defaultExpanded]="false" />
            } @else {
              <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-1">Aucune notification.</div>
            }
          </div>
        </section>

        <!-- Section : Caches & extras -->
        <section class="tw-rounded tw-border tw-border-gray-700/60 tw-bg-gray-900/50">
          <header class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-800/40">
            <i class="ph ph-database tw-text-success-500 tw-text-[13px]"></i>
            <h3 class="tw-text-[12px] tw-font-semibold tw-text-gray-200">Caches & extras</h3>
          </header>
          <div class="tw-p-2">
            @if (snapshot().extras) {
              <dev-json-tree [value]="snapshot().extras" [search]="search()" [maxExpandDepth]="expandDepth()" [defaultExpanded]="true" />
            } @else {
              <div class="tw-text-[11px] tw-text-gray-500 tw-italic tw-px-1">Aucune information supplémentaire exposée.</div>
            }
          </div>
        </section>
      </div>
    </div>
  `,
})
export class DevToolsStateTabComponent {
  private readonly devTools = inject(DevToolsService);

  readonly search = signal<string>('');
  readonly autoRefresh = signal<boolean>(true);
  readonly manualTick = signal<number>(0);
  readonly expandDepth = signal<number>(2);

  private readonly openTasks = signal<Set<number>>(new Set());
  private readonly openPayments = signal<Set<number>>(new Set());

  /** Snapshot réactif : dépend du tick auto et du tick manuel. */
  readonly snapshot = computed(() => {
    // touch reactive dependencies
    if (this.autoRefresh()) {
      this.devTools.refreshTick();
    }
    this.manualTick();
    return this.devTools.snapshot();
  });

  readonly counts = computed(() => {
    const s = this.snapshot();
    return {
      tasks: s.tasks.length,
      payments: s.payments.length,
      notifications: s.notifications.length,
    };
  });

  readonly demarcheRef = computed(() => {
    const d = this.snapshot().demarche as { reference?: string; xref?: string; id?: string } | null;
    return d?.reference ?? d?.xref ?? d?.id ?? null;
  });

  readonly contextLabel = computed<string>(() => {
    const extras = this.snapshot().extras;
    if (extras?.['context'] === 'guest-consultation') {
      return 'Guest / Consultation';
    }
    if (this.snapshot().demarche) {
      return 'Admin / Démarches';
    }
    return 'Inconnu';
  });

  onExpandDepthChange(ev: Event): void {
    const val = Number((ev.target as HTMLSelectElement).value);
    if (!isNaN(val)) {
      this.expandDepth.set(val);
    }
  }

  readonly ageLabel = computed(() => {
    // recompute each tick
    this.devTools.refreshTick();
    const diff = Math.max(0, Date.now() - this.snapshot().capturedAt);
    if (diff < 1000) return `${diff} ms`;
    if (diff < 60_000) return `${Math.floor(diff / 1000)} s`;
    return `${Math.floor(diff / 60_000)} min`;
  });

  readonly capturedAtLabel = computed(() => {
    const ts = this.snapshot().capturedAt;
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return String(ts);
    }
  });

  onSearch(ev: Event): void {
    this.search.set((ev.target as HTMLInputElement).value);
  }

  onToggleAutoRefresh(ev: Event): void {
    const checked = (ev.target as HTMLInputElement).checked;
    this.autoRefresh.set(checked);
    if (checked) {
      this.devTools.startAutoRefresh(500);
    } else {
      this.devTools.stopAutoRefresh();
    }
  }

  manualRefresh(): void {
    this.manualTick.update((v) => v + 1);
  }

  copyAll(): void {
    const json = this.devTools.serialize(this.snapshot());
    try {
      navigator.clipboard?.writeText(json);
    } catch {
      /* ignore */
    }
  }

  download(): void {
    try {
      const json = this.devTools.serialize(this.snapshot());
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devtools-snapshot-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      /* ignore */
    }
  }

  toggleTask(i: number): void {
    const next = new Set(this.openTasks());
    if (next.has(i)) next.delete(i);
    else next.add(i);
    this.openTasks.set(next);
  }
  isTaskOpen(i: number): boolean {
    return this.openTasks().has(i);
  }
  togglePayment(i: number): void {
    const next = new Set(this.openPayments());
    if (next.has(i)) next.delete(i);
    else next.add(i);
    this.openPayments.set(next);
  }
  isPaymentOpen(i: number): boolean {
    return this.openPayments().has(i);
  }

  taskField(obj: unknown, field: string): unknown {
    if (obj && typeof obj === 'object') {
      return (obj as Record<string, unknown>)[field];
    }
    return undefined;
  }

  fmtDate(v: unknown): string {
    if (!v) return '—';
    try {
      const d = v instanceof Date ? v : new Date(v as string | number);
      if (isNaN(d.getTime())) return String(v);
      return d.toLocaleTimeString();
    } catch {
      return String(v);
    }
  }

  statusBg(color: unknown): string {
    if (typeof color === 'string' && color.length) return color + '22';
    return 'rgba(148,163,184,0.15)';
  }

  statusFg(color: unknown): string {
    if (typeof color === 'string' && color.length) return color;
    return '#cbd5e1';
  }
}
